import React, { useState, useEffect } from 'react';
import { Typography, Card, DatePicker, Table, Alert, Spin, Button, Space } from 'antd';
import { TableOutlined, CalendarOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Paragraph } = Typography;

export default function DsmMinRawDataPage() {
  const [selectedDate, setSelectedDate] = useState(dayjs().subtract(1, 'day'));
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [error, setError] = useState(null);

  const dateStr = selectedDate.format('YYYY-MM-DD');

  useEffect(() => {
    fetchRawData();
  }, [dateStr]);

  const fetchRawData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/dsm/raw/${dateStr}/`);
      setRawData(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load raw uploaded data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Time Block',
      dataIndex: 'time_block',
      key: 'time_block',
      width: '25%',
      align: 'center',
    },
    {
      title: 'Forecasted Schedule (MAL) (MW)',
      dataIndex: 'forecast_schedule',
      key: 'forecast_schedule',
      align: 'right',
      render: (v) => Number(v).toFixed(2),
    },
    {
      title: 'AVC (MW)',
      dataIndex: 'avc',
      key: 'avc',
      align: 'right',
      render: (v) => Number(v).toFixed(2),
    },
    {
      title: 'Actual Generation (SEMS Provisional) (MW)',
      dataIndex: 'actual_generation',
      key: 'actual_generation',
      align: 'right',
      render: (v) => Number(v).toFixed(2),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1000, margin: '0 auto' }}>
      {/* Header section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(0, 82, 204, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TableOutlined style={{ color: '#0052CC', fontSize: 22 }} />
          </div>
          <div>
            <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
              Raw Ingested Data
            </Title>
            <Paragraph style={{ color: '#64748b', margin: 0, fontSize: 13 }}>
              View raw time block values uploaded from the client Excel file before running DSM penalty calculations.
            </Paragraph>
          </div>
        </div>

        {/* Date Selector */}
        <Space size={12}>
          <CalendarOutlined style={{ color: '#64748b' }} />
          <DatePicker
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            allowClear={false}
            style={{ borderRadius: 8, height: 38 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRawData}
            loading={loading}
            style={{ borderRadius: 8, height: 38 }}
          />
        </Space>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon style={{ marginBottom: 24, borderRadius: 12 }} />
      )}

      {loading ? (
        <div style={{ padding: '100px 0', textAlign: 'center' }}>
          <Spin size="large" tip="Loading raw block records..." />
        </div>
      ) : rawData.length === 0 ? (
        <Alert
          message="No Ingested Data Found"
          description="There is no raw wind energy data uploaded for this date yet. Please go to the Excel Ingestion tab to upload files."
          type="info"
          showIcon
          style={{ borderRadius: 12 }}
        />
      ) : (
        <Card
          bordered={false}
          style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}
          styles={{ body: { padding: 0 } }}
        >
          <Table
            dataSource={rawData}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 24, showSizeChanger: false, position: ['bottomCenter'] }}
            style={{ borderRadius: 16, overflow: 'hidden' }}
          />
        </Card>
      )}
    </div>
  );
}
